// Dependencies
"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: Object.getOwnPropertyDescriptor(all, name).get
    });
}
_export(exports, {
    get objectIdRefinement () {
        return objectIdRefinement;
    },
    get transformDate () {
        return transformDate;
    },
    get transformDateToUTC () {
        return transformDateToUTC;
    },
    get transformDateToUTCString () {
        return transformDateToUTCString;
    },
    get transformEmptyStringToNull () {
        return transformEmptyStringToNull;
    },
    get transformNullishDate () {
        return transformNullishDate;
    },
    get transformObjectId () {
        return transformObjectId;
    },
    get transformToBoolean () {
        return transformToBoolean;
    },
    get transformValidationErrorMessages () {
        return transformValidationErrorMessages;
    }
});
const _bson = require("bson");
const _zod = require("zod");
_export_star(require("./api-helpers"), exports);
function _export_star(from, to) {
    Object.keys(from).forEach(function(k) {
        if (k !== "default" && !Object.prototype.hasOwnProperty.call(to, k)) {
            Object.defineProperty(to, k, {
                enumerable: true,
                get: function() {
                    return from[k];
                }
            });
        }
    });
    return from;
}
const objectIdRefinement = (value)=>{
    try {
        return new _bson.ObjectId(value).toHexString() === value;
    } catch  {
        return false;
    }
};
const transformObjectId = _zod.z.union([
    _zod.z.string().transform((id, ctx)=>{
        try {
            const objectId = new _bson.ObjectId(id);
            if (objectId.toHexString() !== id) {
                throw new Error("Invalid ObjectId");
            }
            return objectId;
        } catch  {
            ctx.addIssue({
                code: _zod.z.ZodIssueCode.custom,
                message: "Invalid ObjectId"
            });
        }
        return _zod.z.NEVER;
    }),
    _zod.z.instanceof(_bson.ObjectId)
], {
    error: "Invalid ObjectId"
});
const transformDate = _zod.z.preprocess((input)=>{
    if (input instanceof Date) return input;
    if (typeof input === "string") {
        const dateObject = new Date(input);
        if (!isNaN(dateObject.getTime())) {
            return dateObject;
        }
    }
    return input;
}, _zod.z.date({
    error: (issue)=>{
        if (issue.code === "invalid_type") {
            return "Invalid date";
        }
        return undefined;
    }
}));
const transformNullishDate = _zod.z.string().nullish().transform((date, ctx)=>{
    if (!date) {
        return null;
    }
    try {
        const dateObject = new Date(date);
        if (!_zod.z.date().safeParse(dateObject).success) {
            throw new Error("Invalid Date");
        }
        return dateObject;
    } catch  {
        ctx.addIssue({
            code: _zod.z.ZodIssueCode.invalid_type,
            expected: "date",
            received: typeof date
        });
    }
    return _zod.z.NEVER;
});
const isZodIssueInput = (value)=>{
    return typeof value === "object" && value !== null && "path" in value && Array.isArray(value.path) && "message" in value && typeof value.message === "string";
};
const transformValidationErrorMessages = (issues)=>{
    const errorMessages = [];
    for (const issue of issues){
        if (!isZodIssueInput(issue)) {
            continue;
        }
        // Create a copy of the path to avoid mutating the original
        const pathCopy = [
            ...issue.path
        ];
        const userIndex = pathCopy.shift();
        const remainingPath = pathCopy.filter((p)=>typeof p !== "symbol");
        errorMessages.push({
            index: typeof userIndex === "symbol" ? undefined : userIndex,
            path: remainingPath,
            message: issue.message
        });
    }
    return errorMessages;
};
const transformToBoolean = _zod.z.union([
    _zod.z.boolean(),
    _zod.z.string()
]).transform((val, ctx)=>{
    if (typeof val === "boolean") return val;
    const lowercaseVal = val.toLowerCase().trim();
    if (lowercaseVal === "true") {
        return true;
    }
    if (lowercaseVal === "false") {
        return false;
    }
    ctx.addIssue({
        code: _zod.z.ZodIssueCode.custom,
        message: `Invalid boolean string. Expected "true" or "false" (case insensitive), got "${val}"`
    });
    return _zod.z.NEVER;
});
const transformEmptyStringToNull = _zod.z.string().transform((val)=>{
    if (val === "") {
        return null;
    }
    return val;
});
// Function to convert a Date to a UTC Date
function convertDateToUTC(date) {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds()));
}
const transformDateToUTC = _zod.z.union([
    _zod.z.string(),
    _zod.z.date()
]).describe("Accepts a string or Date object and converts it to a UTC Date object.").refine((value)=>{
    // Custom validation logic to ensure the input is a valid date
    let date;
    if (typeof value === "string") {
        // If the input is a string, try to parse it into a Date object
        date = new Date(value);
    } else if (value instanceof Date) {
        // If the input is already a Date object, use it directly
        date = value;
    }
    // Check if the parsed date is valid
    return date && !Number.isNaN(date.getTime());
}, {
    message: "Invalid date format. Must be a valid date string or Date object."
}).transform((value)=>{
    // Transform the validated input into a UTC Date object
    let date;
    if (typeof value === "string") {
        // Parse string input into a Date object
        date = new Date(value);
    } else if (value instanceof Date) {
        // Use the Date object directly
        date = value;
    }
    // Convert the Date object to UTC
    return date && convertDateToUTC(date);
}).describe("Transforms a string or Date object to a UTC Date object.");
const transformDateToUTCString = _zod.z.union([
    _zod.z.date().transform((date)=>convertDateToUTC(date).toISOString()),
    _zod.z.string()
]);
